import { ErrorBoundary } from '@/components/ErrorBoundary'
import Header from '@/components/Header'
import QRScanner from '@/components/QRScanner'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { CASH_CONVERSION_FEE, SELL_TOKEN_ABBR } from '@/constants'
import { useToast } from '@/hooks/use-toast'
import { apiFetch } from '@/utils'
import { zodResolver } from '@hookform/resolvers/zod'
import React, { useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import { mutate } from 'swr'
import Decimal from 'decimal.js'

import { z } from 'zod'
import { IS_TESTNET } from '@/constants/walletConnectionConfig'
import { useSellTokenBalance } from '@/hooks/use-sell-token-balance'
import { useUser } from '@/hooks/use-user'
import { Loader2 } from 'lucide-react'
import { useCashToTokenCalculations } from '@/hooks/use-cash-to-token-calculations'

const convertFormSchema = z.object({
  amount: z
    .string()
    .refine(
      (value) => Number(value) >= 0.001,
      'Cash amount should be at least 0.001 USD'
    ),
  customerAddress: z.string().regex(/^0x[a-fA-F0-9]{40}$/, {
    message:
      'Invalid wallet address format. Please enter a valid Ethereum address.'
  })
})

interface ISwapInfo {
  fromTokenAmount: string
  toTokenAmount: string
}

const CashToTokenPage: React.FC = () => {
  const form = useForm<z.infer<typeof convertFormSchema>>({
    resolver: zodResolver(convertFormSchema),
    defaultValues: {
      amount: '',
      customerAddress: ''
    }
  })
  const { toast } = useToast()
  const { walletAddress, updateBalance: updateSECHBalance } = useUser()
  const swapTokenBalanceData = useSellTokenBalance(walletAddress)

  const [isProcessing, setIsProcessing] = useState(false)
  const [isPriceLoading, setIsPriceLoading] = useState(false)
  const [sechAmount, setSechAmount] = useState(0)
  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(
    null
  )
  const [swapInfo, setSwapInfo] = useState<ISwapInfo>({} as ISwapInfo)

  const amount = form.watch('amount')

  const {
    feeAmount,
    netAmount,
    exchangeRate,
    userReceive,
    adminSpend,
    adminBalance,
    isBalanceInsufficient
  } = useCashToTokenCalculations({
    amount,
    sechAmount,
    swapInfo,
    swapTokenBalanceData
  })

  const handleWalletScanned = (address: string) => {
    form.setValue('customerAddress', address)
  }

  const getSECHAmount = useCallback(
    async (usdAmount?: string) => {
      const exchangeInfoResponse = await apiFetch(
        `/v1/cash-swap-amounts?amountIn=${usdAmount || amount}`
      )

      const { data } = await exchangeInfoResponse.json()
      console.log('Swap amounts result:\n', { data })
      setSechAmount(Number(data?.toTokenAmount))
      setSwapInfo({
        fromTokenAmount: data?.fromTokenAmount,
        toTokenAmount: data?.toTokenAmount
      })
    },
    [amount]
  )

  const handleCashAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const usdAmount = e.target.value

    if (/^\d+\.\d{4,}$/.test(usdAmount)) {
      return // Restrict input to a maximum of 3 decimal places
    }

    form.setValue('amount', usdAmount)
    form.clearErrors('amount')

    if (!parseFloat(usdAmount)) {
      setSechAmount(0)
      return
    }
    setIsPriceLoading(true)
    if (debounceTimeout) clearTimeout(debounceTimeout)

    setDebounceTimeout(
      setTimeout(() => {
        getSECHAmount(usdAmount)
          .catch((error) => {
            console.error('Failed to fetch tokens amount. ', error)
          })
          .finally(() => {
            setIsPriceLoading(false)
          })
      }, 1000)
    )
  }

  const onSubmit = async (
    cashExchangeValues: z.infer<typeof convertFormSchema>
  ) => {
    try {
      setIsProcessing(true)
      if (!swapInfo.fromTokenAmount || !swapInfo.toTokenAmount) {
        throw new Error('Missing necessary data for cash conversion')
      }
      setIsProcessing(true)

      const response = await apiFetch('/v1/admin/cash-to-token', {
        withCredentials: true,
        method: 'POST',
        body: JSON.stringify({
          cashAmount: amount,
          recipientAddress: cashExchangeValues.customerAddress
        })
      })

      const result = await response?.json()
      if (response.ok) {
        toast({
          title: 'Swap Successful',
          description:
            'Tokens have been successfully exchanged and sent to the user wallet'
        })
        form.reset()
        await Promise.all([
          updateSECHBalance(),
          swapTokenBalanceData?.refetchSellTokenBalance()
        ])
      } else {
        console.log({ result, response })

        let errorMessage
        if (Object.keys(result?.error?.message?.fieldErrors || {})?.length) {
          errorMessage = Object.values(result?.error?.message?.fieldErrors)
            .flat()
            .join(', ')
        } else {
          errorMessage =
            result?.error?.message ||
            `Code: ${result?.error?.code}. Unexpected error occurred`
        }
        toast({
          title: 'Swap Failed',
          description: errorMessage,
          variant: 'destructive'
        })
      }
      mutate(`/v1/transactions`)
    } catch (error: any) {
      console.error(error)
      toast({
        title: error?.title || 'Error',
        description: `Failed to swap tokens. ${
          error?.details || error?.message
        }`,
        variant: 'destructive'
      })
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <>
      <Header />
      <div className="max-w-5xl mx-auto px-5 pb-8">
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">
            Cash to SECH Conversion
          </h2>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="customerAddress"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Customer Wallet Address</FormLabel>
                      <div className="flex flex-col items-center justify-start gap-4 md:flex-row justify-center">
                        <FormControl>
                          <Input placeholder="0x..." {...field} />
                        </FormControl>
                        <ErrorBoundary>
                          <QRScanner
                            mode="wallet"
                            onWalletScanned={handleWalletScanned}
                          />
                        </ErrorBoundary>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="amount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Cash Amount (USD)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="any"
                          placeholder={`Amount of cash to swap...`}
                          {...field}
                          onChange={handleCashAmountChange}
                        />
                      </FormControl>
                      <FormMessage />
                      {!isPriceLoading && !!amount && (
                        <div className="pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                          <Card className="text-sm text-muted-foreground px-3 py-2 space-y-1">
                            <h4 className="mb-2 text-lg font-semibold text-foreground">
                              Transaction Details
                            </h4>

                            <div>
                              Fee ({CASH_CONVERSION_FEE}%): ${feeAmount} USD
                            </div>
                            <div>Net amount: ${netAmount}</div>
                            <div>Exchange rate: 1 SECH - ${exchangeRate}</div>
                            <div className="font-semibold text-foreground">
                              User will receive ~{userReceive} SECH{' '}
                            </div>
                          </Card>
                          <Card className="text-sm text-muted-foreground px-3 py-2 space-y-2.5">
                            <h4 className="mb-2 text-lg font-semibold text-foreground">
                              Admin Wallet Impact
                            </h4>

                            <div>
                              <div>Your balance:</div>
                              <div>
                                {adminBalance} {SELL_TOKEN_ABBR}
                              </div>
                            </div>
                            <div>
                              <div className="font-semibold text-foreground">
                                You will spend:
                              </div>
                              <div className="font-semibold text-foreground">
                                ~{adminSpend} {SELL_TOKEN_ABBR}
                              </div>
                            </div>
                          </Card>
                        </div>
                      )}
                    </FormItem>
                  )}
                />
              </div>

              <Button
                type="submit"
                disabled={
                  isProcessing || isPriceLoading || isBalanceInsufficient
                }
                className="w-full">
                {isProcessing ? (
                  <>
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : isPriceLoading ? (
                  <>
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                    Calculating tokens amount...
                  </>
                ) : isBalanceInsufficient ? (
                  'Insufficient Balance'
                ) : (
                  'Process Conversion'
                )}
              </Button>
            </form>
          </Form>
        </Card>
      </div>
    </>
  )
}

export default CashToTokenPage
